/**
 * Primitive schemas reused across every other schema file.
 *
 * Objects are deliberately NOT `.strict()`: unknown body keys are stripped,
 * never rejected, so an older client keeps working (04 §9.2).
 */
import { z } from 'zod';
import { STUDENT_ID_PATTERN } from '../constants.js';
import { ERROR_CODES } from '../error-codes.js';
/** Every id in the system is a UUID v7 string. */
export const UuidSchema = z.string().uuid();
/** ISO-8601 UTC timestamp, as every API response formats one. */
export const IsoDateTimeSchema = z.string().datetime({ offset: true });
/** Trimmed, non-empty human text. */
export const NonEmptyStringSchema = z.string().trim().min(1);
/** WGS-84 latitude. */
export const LatSchema = z.number().min(-90).max(90);
/** WGS-84 longitude. */
export const LngSchema = z.number().min(-180).max(180);
/** Academic year, e.g. `2025/2026` (04 §4.3). */
export const AcademicYearSchema = z
    .string()
    .trim()
    .regex(/^\d{4}\/\d{4}$/, 'ACADEMIC_YEAR_FORMAT');
/**
 * A student's `university_id`. The API may narrow this with its own
 * `STUDENT_ID_PATTERN` env value; the default expression is the same one.
 */
export const UniversityIdSchema = z
    .string()
    .trim()
    .regex(new RegExp(STUDENT_ID_PATTERN), 'BAD_UNIVERSITY_ID');
/** Password rules shared by every password-carrying route (04 §3.3). */
export const PasswordSchema = z.string().min(8).max(128);
/** The 6-digit one-time reset code a TA reads out to a student (04 §4.1). */
export const ResetCodeSchema = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'RESET_CODE_FORMAT');
/**
 * The error body of every REST route and socket ack (04 §9.3).
 * `code` is checked against the shared enum so a typo in a fixture fails a test
 * rather than reaching a frontend that has no translation for it.
 */
export const ErrorEnvelopeSchema = z.object({
    code: z.enum(ERROR_CODES),
    message: z.string(),
    details: z.unknown().optional(),
    requestId: z.string().optional(),
});
/**
 * A boolean that survives a query string or a multipart field.
 * `z.coerce.boolean()` is useless here: it turns the string `"false"` into
 * `true`, which is exactly the bug this schema exists to avoid.
 */
export const BooleanishSchema = z
    .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
    .transform((value) => value === true || value === 'true' || value === '1');
/** `{ id }` path parameter. */
export const IdParamSchema = z.object({ id: UuidSchema });
//# sourceMappingURL=common.js.map